import { Property } from 'csstype';
import { Geist_Mono, Gilda_Display, Hanken_Grotesk } from 'next/font/google';
import React from 'react';

import { PageBackgroundImageLayer } from '@/components/PageBackground';

import { WebsiteBackground } from '@cineamo/api/models/website-configuration/WebsiteBackground.enum';
import { WebsiteFont } from '@cineamo/api/models/website-configuration/WebsiteFont.enum';
import { WebsiteTheme } from '@cineamo/api/models/website-configuration/WebsiteTheme.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import {
    ID_BELLARIA_KINO,
    ID_CINEMOON,
    ID_KINO_FREISTADT,
    ID_KINOSOMMER_VILLACH,
    ID_LUXOR_FILMPALAST_BENSHEIM,
    ID_LUXOR_FILMPALAST_HEIDELBERG,
    ID_LUXOR_FILMPALAST_NIDDERAU,
    ID_LUXOR_FILMPALAST_SCHWETZINGEN,
    ID_LUXOR_FILMPALAST_WALLDORF,
    ID_OPEN_AIR_KINO_AUF_DEM_SCHLOSSBERG_BOXBERG,
    ID_OPEN_AIR_KINO_IM_WILDPARK_BAD_MERGENTHEIM,
    ID_OPEN_AIR_KINO_LANGEN,
    ID_PRESTIGE,
    ID_SCHANZENKINO_OPEN_AIR,
    ID_TRAUMPALAST_BACKNANG,
    ID_TRAUMPALAST_ESSLINGEN,
    ID_TRAUMPALAST_LEONBERG,
    ID_TRAUMPALAST_NUERTINGEN,
    ID_TRAUMPALAST_SCHORNDORF,
    ID_TRAUMPALAST_SCHWAEBISCH_GMUEND,
    ID_TRAUMPALAST_WAIBLINGEN
} from '@cineamo/api/constants/cinema-ids';
import {
    WEBSITE_BACKGROUND_DEFAULT_OPACITY,
    WEBSITE_BACKGROUND_PRESETS
} from '@cineamo/ui/src/constants/website-background-constants';

const geistMono = Geist_Mono({ weight: '400', subsets: ['latin'] });
const gildaDisplay = Gilda_Display({ weight: '400', subsets: ['latin'] });
const hankenGrotesk = Hanken_Grotesk({ weight: ['300', '400', '500', '600', '700'], subsets: ['latin'] });

// TODO: Maybe 'overriding styles' can be done easier / more satisfying?

// GeistMono: Here we need to add some overrides because the font might appear bigger or smaller compared
// to our default font montserrat. So we need to tweak the numbers a little bit.
const fontGeistMono: string = `
    body {
        font-family: ${geistMono.style.fontFamily} !important;
        letter-spacing: 3% !important;
    }
    .font-montserrat-medium {
        font-weight: 400 !important;
    }
    .font-bold,
    .font-montserrat-bold {
        font-weight: 600 !important;
    }
`;

// GildaDisplay: Here we need to add some overrides because the font might appear bigger or smaller compared
// to our default font montserrat. So we need to tweak the numbers a little bit.
const fontGildaDisplayHeroTitle: string = `
    .text-headline-24-custom-style {
        font-size: 28px;
        line-height: 32px;
        @media (min-width: 700px){
            font-size: 32px;
            line-height: 36px;
        }
        font-family: ${gildaDisplay.style.fontFamily};
    }
`;

const fontGildaDisplaySectionTitle: string = `
    .text-overline-16-custom-style {
        font-size: 20px;
        line-height: 20px;
        @media (min-width: 700px){
            font-size: 24px;
            line-height: 24px;
        }
        font-family: ${gildaDisplay.style.fontFamily};
        text-transform: unset;
    }
`;

const fontGildaDisplay: string = fontGildaDisplayHeroTitle + fontGildaDisplaySectionTitle;

// HankenGrotesk: Here we need to add some overrides because the font might appear bigger or smaller compared
// to our default font montserrat. So we need to tweak the numbers a little bit.
const fontHankenGrotesk: string = `
    body {
        font-family: ${hankenGrotesk.style.fontFamily} !important;
    }
`;

export type CustomStyle = {
    backgroundImage?: string;
    backgroundRepeat?: Property.BackgroundRepeat | undefined;
    backgroundSize?: Property.BackgroundSize | undefined;
    backgroundPosition?: Property.BackgroundPosition | undefined;
    background?: string;
    opacity?: string;
    heroBackgroundColor?: string;
    font?: string;
};

const FONT_FAMILIES: Record<WebsiteFont, string> = {
    [WebsiteFont.MONTSERRAT]: 'Montserrat, Arial, sans-serif',
    [WebsiteFont.GEIST_MONO]: geistMono.style.fontFamily,
    [WebsiteFont.GILDA_DISPLAY]: gildaDisplay.style.fontFamily,
    [WebsiteFont.HANKEN_GROTESK]: hankenGrotesk.style.fontFamily
};

/**
 * CSS when a font is configured as the default font of the entire website.
 * `MONTSERRAT` is the app default and intentionally maps to no override;
 * GeistMono keeps its static letter-spacing/weight tweaks.
 */
function getDefaultFontCss(font: WebsiteFont): string | undefined {
    switch (font) {
        case WebsiteFont.MONTSERRAT:
            return undefined;
        case WebsiteFont.GEIST_MONO:
            return fontGeistMono;
        case WebsiteFont.HANKEN_GROTESK:
            return fontHankenGrotesk;
        default:
            return `
    body {
        font-family: ${FONT_FAMILIES[font]} !important;
    }
`;
    }
}

/** CSS when a font is configured for the hero slider title, on top of the default font. */
function getHeroTitleFontCss(font: WebsiteFont): string {
    if (font === WebsiteFont.GILDA_DISPLAY) {
        return fontGildaDisplayHeroTitle;
    }
    return `
    .text-headline-24-custom-style {
        font-family: ${FONT_FAMILIES[font]} !important;
    }
`;
}

/** CSS when a font is configured for section titles, on top of the default font. */
function getSectionTitleFontCss(font: WebsiteFont): string {
    if (font === WebsiteFont.GILDA_DISPLAY) {
        return fontGildaDisplaySectionTitle;
    }
    return `
    .text-overline-16-custom-style {
        font-family: ${FONT_FAMILIES[font]} !important;
    }
`;
}

/**
 * Font CSS configured via `websiteConfiguration.theme`. Returns undefined
 * when no font is configured at all, so the hardcoded per-cinema defaults
 * still apply; an empty string means "explicitly the app default".
 */
function getThemeFontCss(theme: WebsiteTheme | undefined): string | undefined {
    if (!theme?.websiteFont && !theme?.heroTitleFont && !theme?.sectionTitleFont) {
        return undefined;
    }

    return [
        theme.websiteFont ? getDefaultFontCss(theme.websiteFont) : undefined,
        theme.heroTitleFont ? getHeroTitleFontCss(theme.heroTitleFont) : undefined,
        theme.sectionTitleFont ? getSectionTitleFontCss(theme.sectionTitleFont) : undefined
    ]
        .filter(Boolean)
        .join('\n');
}

/**
 * Background configured by the cinema via `websiteConfiguration.theme`.
 * Returns null when no background is configured, so the hardcoded
 * per-cinema defaults below still apply.
 */
function getThemeCustomStyle(theme: WebsiteTheme | undefined): CustomStyle | null {
    const websiteBackground = theme?.websiteBackground;

    if (!websiteBackground) {
        return null;
    }

    if (websiteBackground === WebsiteBackground.NONE) {
        return {
            heroBackgroundColor: theme.heroBackgroundColor
        };
    }

    const preset = WEBSITE_BACKGROUND_PRESETS[websiteBackground];
    const backgroundImage =
        websiteBackground === WebsiteBackground.CUSTOM ? theme.backgroundImageUrl : preset?.imagePath;

    return {
        backgroundImage,
        backgroundRepeat: theme.backgroundRepeat ?? preset?.backgroundRepeat,
        backgroundSize: theme.backgroundSize ?? preset?.backgroundSize,
        backgroundPosition: theme.backgroundPosition,
        background: theme.background ?? preset?.background,
        opacity: `${theme.backgroundOpacity ?? preset?.backgroundOpacity ?? WEBSITE_BACKGROUND_DEFAULT_OPACITY}%`,
        heroBackgroundColor: theme.heroBackgroundColor
    };
}

function getLegacyCustomStyle(cinemaId?: number): CustomStyle | null {
    switch (cinemaId) {
        case ID_LUXOR_FILMPALAST_BENSHEIM: // luxor-filmpalast-bensheim
        case ID_LUXOR_FILMPALAST_SCHWETZINGEN: // luxor-filmpalast-schwetzingen
        case ID_LUXOR_FILMPALAST_NIDDERAU: // luxor-filmpalast-nidderau
        case ID_LUXOR_FILMPALAST_WALLDORF: // luxor-filmpalast-walldorf
        case ID_LUXOR_FILMPALAST_HEIDELBERG: // luxor-filmpalast-heidelberg
            return {
                backgroundImage: '/bg-carbon.jpg',
                background: 'radial-gradient(#2B2B2B, #121212)',
                backgroundRepeat: 'repeat',
                opacity: '15%',
                font: fontGildaDisplay
            };
        case ID_TRAUMPALAST_BACKNANG: //    Traumpalast Backnang
        case ID_TRAUMPALAST_NUERTINGEN: //    Traumpalast Nürtingen
        case ID_TRAUMPALAST_SCHORNDORF: //    Traumpalast Schorndorf
        case ID_TRAUMPALAST_WAIBLINGEN: //    Traumpalast Waiblingen
        case ID_TRAUMPALAST_ESSLINGEN: //    Traumpalast Esslingen
        case ID_TRAUMPALAST_LEONBERG: //   Traumpalast Leonberg
        case ID_TRAUMPALAST_SCHWAEBISCH_GMUEND: //   Traumpalast Schwäbisch Gmünd
            return {
                backgroundImage: '/traumpalast-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '15%'
            };
        case ID_KINO_FREISTADT:
            return {
                font: fontHankenGrotesk
            };
        case ID_SCHANZENKINO_OPEN_AIR: //   Schanzenkino Open-Air
        case ID_KINOSOMMER_VILLACH: //   Kinosommer Villach
            return {
                backgroundImage: '/kinosommer-villach-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '15%'
            };
        case ID_OPEN_AIR_KINO_LANGEN: //   Open-Air-Kino Langen
            return {
                backgroundImage: '/kinosommer-villach-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '15%'
            };
        case ID_OPEN_AIR_KINO_IM_WILDPARK_BAD_MERGENTHEIM: //   Open-Air-Kino im Wildpark Bad Mergentheim
            return {
                backgroundImage: '/kinosommer-villach-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '15%'
            };
        case ID_OPEN_AIR_KINO_AUF_DEM_SCHLOSSBERG_BOXBERG: //   Open-Air Kino auf dem Schlossberg Boxberg
            return {
                backgroundImage: '/kinosommer-villach-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '15%'
            };
        case ID_CINEMOON:
            return {
                backgroundImage: '/kinosommer-villach-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '15%'
            };
        case ID_PRESTIGE:
            return {
                backgroundImage: '/prestige-kuenzelsau-background.png',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                opacity: '25%',
                heroBackgroundColor: '#0A0F11'
            };
        case ID_BELLARIA_KINO:
            return {
                font: fontGeistMono
            };
        default:
            return null;
    }
}

export function getCustomStyle(domainConfiguration?: DomainConfigurationDto): CustomStyle | null {
    const theme = domainConfiguration?.websiteConfiguration?.theme;
    const legacyStyle = getLegacyCustomStyle(domainConfiguration?.cinema?.id);
    const themeStyle = getThemeCustomStyle(theme);

    // Configured fonts win over the hardcoded per-cinema default —
    // including MONTSERRAT, which explicitly resets to the app default.
    const themeFontCss = getThemeFontCss(theme);
    const font = themeFontCss !== undefined ? themeFontCss : legacyStyle?.font;

    if (!themeStyle) {
        return legacyStyle ? { ...legacyStyle, font } : font ? { font } : null;
    }

    // A configured theme fully controls the background; the hero background
    // color still falls back to the hardcoded default unless overridden.
    return {
        ...themeStyle,
        font,
        heroBackgroundColor: themeStyle.heroBackgroundColor ?? legacyStyle?.heroBackgroundColor
    };
}

export default function CustomStyles({
    domainConfiguration
}: {
    domainConfiguration: DomainConfigurationDto;
}): React.JSX.Element | null {
    const customStyle = getCustomStyle(domainConfiguration);

    if (!customStyle) {
        return null;
    }

    return (
        <>
            {customStyle.font && (
                <style
                    dangerouslySetInnerHTML={{
                        __html: customStyle.font
                    }}
                />
            )}

            <PageBackgroundImageLayer customStyle={customStyle} />
        </>
    );
}
